import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Crown, FileText, HeartPulse, Mic } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export const TRIAL_INTENT = "rpopro_trial_intent";

const STEPS = [
  { icon: HeartPulse, title: "Well-Being", text: "A calm companion for the hard days: daily boosts, journeys, a journal and voice chat that keep you steady.", to: "/wellbeing" },
  { icon: FileText, title: "Resume Studio", text: "Rebuild your resume for ATS and AI screeners, then tailor a version for every job in one click.", to: "/resume-studio" },
  { icon: Mic, title: "Mock interviews", text: "Practise with an AI interviewer tuned to your resume and get scored feedback on every answer.", to: "/ai-studio" },
];

const btn = "rounded-full px-5 py-2.5 text-sm font-semibold transition-shadow";
const purple = "bg-gradient-to-r from-[#9333EA] via-[#C084FC] to-[#9333EA] text-white shadow-[0_0_30px_rgba(168,85,247,0.5)] hover:shadow-[0_0_44px_rgba(192,132,252,0.7)]";

const Intro = ({ ends }) => (
  <>
    <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#C084FC]/50 bg-[#A855F7]/15"><Crown className="h-7 w-7 text-[#E9D5FF]" /></div>
    <DialogTitle className="font-display text-2xl text-zinc-50">Your <span className="trial-glow-text">free 7-day Elite trial</span> is on</DialogTitle>
    <DialogDescription className="text-sm text-zinc-400" data-testid="trial-welcome-ends">Every Elite feature is unlocked{ends ? ` until ${ends}` : ""}. No card on file. Here's a 3-step tour of the best of it.</DialogDescription>
  </>
);

const Step = ({ s, i }) => (
  <div className="fade-up" key={s.title} data-testid={`trial-tour-step-${i + 1}`}>
    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#C084FC]/50 bg-[#A855F7]/15"><s.icon className="h-7 w-7 text-[#E9D5FF]" /></div>
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C084FC]">Step {i + 1} of 3</div>
    <DialogTitle className="mt-1 font-display text-2xl text-zinc-50">{s.title}</DialogTitle>
    <DialogDescription className="mt-2 text-sm text-zinc-300">{s.text}</DialogDescription>
  </div>
);

export const TrialWelcome = () => {
  const { status, billing } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(-1);
  const navigate = useNavigate();
  useEffect(() => {
    if (status !== "authed" || !billing || !localStorage.getItem(TRIAL_INTENT)) return;
    localStorage.removeItem(TRIAL_INTENT);
    if (billing.trial_active) setOpen(true);
  }, [status, billing]);
  const ends = billing?.trial_ends_at ? new Date(billing.trial_ends_at).toLocaleDateString([], { month: "long", day: "numeric" }) : null;
  const go = (to) => { setOpen(false); navigate(to); };
  const last = step === STEPS.length - 1;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md border-[#A855F7]/50 bg-gradient-to-b from-[#1a0b2e] to-[#09090B] text-center" data-testid="trial-welcome">
        <span aria-hidden className="trial-glow pointer-events-none absolute inset-0 rounded-[inherit]" />
        {step < 0 ? <Intro ends={ends} /> : <Step s={STEPS[step]} i={step} />}
        <div className="mt-2 flex justify-center gap-1.5" data-testid="trial-tour-dots">
          {STEPS.map((s, i) => <span key={s.title} className={cn("h-1.5 rounded-full transition-[width,background-color] duration-300", i === step ? "w-6 bg-[#C084FC]" : "w-1.5 bg-zinc-700")} />)}
        </div>
        {step < 0 ? (
          <div className="mt-2 flex flex-col gap-2">
            <button onClick={() => setStep(0)} className={cn(btn, purple)} data-testid="trial-tour-start">Show me around</button>
            <button onClick={() => go("/search")} className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="trial-tour-skip">Skip, take me to job search</button>
          </div>
        ) : (
          <div className="mt-2 flex items-center justify-between gap-2">
            <button onClick={() => go(STEPS[step].to)} className={cn(btn, "border border-[#C084FC]/50 text-[#E9D5FF] hover:bg-[#A855F7]/15")} data-testid="trial-tour-try">Try it now</button>
            <button onClick={() => (last ? go("/search") : setStep(step + 1))} className={cn(btn, purple, "inline-flex items-center gap-1.5")} data-testid="trial-tour-next">
              {last ? "Finish" : "Next"}<ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
